import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import WorkflowStepper, {
  type WorkflowStep,
  type WorkflowStepState,
} from "../../shared/Workflow/WorkflowStepper";
import Modal from "../../../layouts/Modal";
import LeadCheckInModal from "./LeadCheckInModal";
import useCheckIn from "../../../data/useCheckIn";
import useNotification from "../../notifications/useNotifications";
import useLeadMutations from "../../../data/useLeadMutations";
import { ICompleteLeadResponse, ILead, LeadStatus, PaymentStatus } from "../../../models/Lead";
import usePayments from "../../../data/usePayments";
import useInvoices from "../../../data/useInvoices";
import { useNavigate } from "react-router-dom";
import CODStatus from "./CODStatus";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useLeads, { collectFailureText } from "../../../data/useLeads";
import { codStepState, isCodStepFinished } from "../../../utils/codStepState";
import { collectedOnForMarkCollected } from "../../../utils/collectedOn";

export default function LeadWorkflowCard({ lead }: { lead: ILead }) {
  const vehicleId = lead.vehicleId ?? lead.vehicle?.id ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { saveHandlers: invoiceSaveHandlers } = useDefaultCRUDHandlers("Invoice");
  const { saveHandlers: leadSaveHandlers } = useDefaultCRUDHandlers("Lead");
  // ignoreQuery: true — mutations only, no all-leads fetch.
  const { markLeadCollected } = useLeads(true, "LeadWorkflowCard");
  const { addNotification } = useNotification();
  const { complete, update } = useLeadMutations(lead.id, "LeadWorkflowCard");
  const { sendPayment } = usePayments(false, undefined, undefined, true);
  const { ensureSelfBilledInvoiceForCollection } = useInvoices(undefined, false);

  const [checkInId, setCheckInId] = useState<string | undefined>(lead.vehicle?.checkInId ?? undefined);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [codOpen, setCodOpen] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
  const [paymentMissingOpen, setPaymentMissingOpen] = useState(false);
  const [raisingPayment, setRaisingPayment] = useState(false);
  const [confirmCollectedOpen, setConfirmCollectedOpen] = useState(false);

  const { checkIn, checkInItems, checkInSteps, checkInDocuments, createCheckIn, saveCheckInItem, uploadCheckInItemDocuments, downloadCheckInItemDocument, viewSensitiveDocument, sensitiveDocumentAccess, deleteCheckInItemDocument } = useCheckIn(vehicleId, checkInId);

  useEffect(() => {
    setCheckInId(lead.vehicle?.checkInId ?? undefined);
  }, [lead.vehicle?.checkInId]);

  const checkInStepsData = checkInSteps.data?.data ?? [];
  const checkInItemsData = checkInItems.data?.data ?? [];
  const checkInDocumentsData = checkInDocuments.data?.data ?? [];

  const hasScheduledCollection = !!lead.vehicle?.scheduledCollectionOn;
  const isCollected = !!lead.vehicle?.collectedOn;
  const isCheckInComplete = !!checkIn.data?.data?.completed;
  const isLeadComplete = lead.status === LeadStatus.Completed || !!lead.completedOn;

  const paymentState: WorkflowStepState = useMemo(() => {
    if (lead.paymentStatus === PaymentStatus.Sent || lead.paymentStatus === PaymentStatus.Approved) return "done";
    if (lead.paymentStatus === PaymentStatus.Raised || lead.paymentStatus === PaymentStatus.Failed) return "in_progress";
    return "not_started";
  }, [lead.paymentStatus]);

  const isPaymentDone = paymentState === "done";
  const codState: WorkflowStepState = codStepState(lead.vehicle);
  const isCodDone = isCodStepFinished(codState);
  const isInvoiceDone = !!lead.invoiceId;
  const allParallelDone = isPaymentDone && isCodDone && isInvoiceDone;
  const canAttemptComplete = isCheckInComplete || isLeadComplete;

  const awaitingSchedulingState: WorkflowStepState = hasScheduledCollection || isCollected ? "done" : "in_progress";
  const collectedState: WorkflowStepState = isCollected ? "done" : hasScheduledCollection ? "in_progress" : "not_started";
  const checkInState: WorkflowStepState = isCheckInComplete ? "done" : isCollected ? "in_progress" : "not_started";
  const invoiceState: WorkflowStepState = isInvoiceDone ? "done" : "not_started";
  const completeState: WorkflowStepState = isLeadComplete ? "done" : allParallelDone ? "in_progress" : "not_started";
  const linearProgressThroughParallel = allParallelDone || isLeadComplete;

  const unfinishedSteps = [
    !isPaymentDone ? "Payment" : null,
    !isCodDone ? "COD" : null,
  ].filter(Boolean) as string[];

  const completeConfirmMessage = unfinishedSteps.length > 0
    ? `Some workflow steps are still unfinished: ${unfinishedSteps.join(", ")}.\n\nAre you sure you want to complete this lead now?`
    : "All workflow steps are complete. Mark this lead as complete now?";

  const handleOpenCheckIn = () => {
    if (!vehicleId) return;

    if (checkInId) {
      setCheckInOpen(true);
      return;
    }

    createCheckIn.mutate(
      { vehicleId },
      {
        onSuccess: (res) => {
          setCheckInId(res.data.id);
          setCheckInOpen(true);
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Unable to start check-in",
          });
        },
      }
    );
  };


  const handleConfirmMarkCollected = () => {
    if (!lead.vehicle) return;

    // Send the lead, not the vehicle. One Vehicle row is shared by every lead on the
    // registration, so a vehicle-level update leaves the API to infer which quote was
    // honoured — which risks collecting against an old or cancelled lead and paying the
    // wrong customer. This endpoint stamps this lead and the vehicle together.
    markLeadCollected.mutate(
      { leadId: lead.id, collectedOn: collectedOnForMarkCollected(lead.vehicle.scheduledCollectionOn) },
      {
        onSuccess: () => {
          leadSaveHandlers.onSuccess();
          setConfirmCollectedOpen(false);
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["lead", lead.id] });
          }, 2000);
        },
        onError: (error) => {
          addNotification({ variant: "error", primaryText: collectFailureText(error) });
        },
      }
    );
  };

  const handleCompleteLead = () => {
    setCompleting(true);
    complete.mutate(lead.id, {
      onSuccess: (res: ICompleteLeadResponse) => {
        // The lead completed either way; the message says why the invoice didn't, and what to fix.
        addNotification({
          variant: res.invoiceCreated === false ? "warn" : "success",
          primaryText: res.message ?? "Lead has been completed",
        });
        setConfirmCompleteOpen(false);
      },
      onError: (err: any) => {
        addNotification({
          variant: "error",
          primaryText: err.message ?? "Unable to complete lead",
        });
      },
      onSettled: () => setCompleting(false),
    });
  };

  const hasRequiredPaymentDetails =
    !!lead.accountName &&
    !!lead.accountNumber &&
    !!lead.sortCode &&
    lead.price !== null &&
    lead.price !== undefined &&
    lead.price > 0;

  const handleAwaitingPaymentClick = () => {
    if (paymentState !== "not_started") return;

    if (!hasRequiredPaymentDetails) {
      setPaymentMissingOpen(true);
      return;
    }

    const updatedLead: ILead = {
      ...lead,
      price: lead.price && parseFloat(String(lead.price)),
      commission: lead.commission && parseFloat(String(lead.commission)),
    };

    // Mirrors LeadPaymentDetails' "onSubmit" behavior (update lead first, then ask to raise payment).
    update.mutate(updatedLead, {
      onSuccess: () => {
        setPaymentConfirmOpen(true);
      },
      onError: (err: any) => {
        addNotification({
          variant: "error",
          primaryText: err?.message ?? "Unable to update payment details",
        });
      },
    });
  };

  const handleEnsureSelfBilledInvoice = () => {
    if (!vehicleId) {
      addNotification({
        variant: "warning",
        primaryText: "Vehicle is required to generate a self-billed invoice",
      });
      return;
    }

    ensureSelfBilledInvoiceForCollection.mutate(
      {
        vehicleId,
        leadId: lead.id,
        collectedOn: lead.vehicle?.collectedOn ?? null,
      },
      {
        onSuccess: invoiceSaveHandlers.onSuccess,
        // The server's refusal says what to fix ("The seller has no name…"), which the generic
        // "Failed to save invoice" hid.
        onError: (err: unknown) => {
          addNotification({
            variant: "error",
            primaryText: err instanceof Error ? err.message : "Failed to save invoice",
          });
        },
      }
    );
  };

  const handleRaisePayment = () => {
    setRaisingPayment(true);

    sendPayment.mutate(lead.id, {
      onSuccess: (res: any) => {
        if (res?.success) {
          setPaymentConfirmOpen(false);
          addNotification({
            variant: "success",
            primaryText: res.message ?? "Payment raised",
          });
        } else {
          addNotification({
            variant: "error",
            primaryText: res?.message ?? "Unable to raise payment",
          });
        }
      },
      onError: (e: any) => {
        addNotification({
          variant: "error",
          primaryText: e?.message ?? "Unable to raise payment",
        });
      },
      onSettled: () => setRaisingPayment(false),
    });
  };

  const workflowSteps: WorkflowStep[] = [
    {
      label: "Awaiting Scheduling",
      state: awaitingSchedulingState,
      lineActive: hasScheduledCollection || isCollected,
      onClick: () => navigate("/trip-manager"),
      isClickable: !hasScheduledCollection && !isCollected,
      instructions: "The vehicle has not been scheduled for collection yet. Click to go to the trip manager.",
    },
    {
      label: "Collected",
      state: collectedState,
      lineActive: isCollected,
      onClick: () => {
        if (isCollected || !lead.vehicle) return;
        setConfirmCollectedOpen(true);
      },
      isClickable: !isCollected && !!lead.vehicle,
      instructions: "The vehicle will usually be marked collected by a driver. Click to manually mark it as collected now.",
    },
    {
      label: "Awaiting Check In",
      state: checkInState,
      lineActive: linearProgressThroughParallel,
      onClick: handleOpenCheckIn,
      isClickable: !createCheckIn.isLoading,
      instructions: "Follow the check-in steps and mark them all as done before continuing. Click to open check-in tasks."
    },
    {
      label: "Awaiting Payment",
      state: paymentState,
      lineActive: linearProgressThroughParallel,
      onClick: handleAwaitingPaymentClick,
      isClickable: paymentState === "not_started",
      instructions: paymentState == "in_progress" ? "Payment Raised - waiting for approval." : "Raise and send payment to complete this step. Click to raise payment."
    },
    {
      label: "Awaiting COD",
      state: codState,
      lineActive: linearProgressThroughParallel,
      onClick: () => setCodOpen(true),
      // A skipped COD stays clickable so staff can switch it back on for a vehicle that is being scrapped.
      isClickable: codState !== "done",
      instructions: "Generate a certificate of destruction this vehicle or mark it as not required. Click to manage COD."
    },
    {
      label: "Generate Invoice",
      state: invoiceState,
      lineActive: linearProgressThroughParallel,
      onClick: handleEnsureSelfBilledInvoice,
      isClickable: !!vehicleId && !ensureSelfBilledInvoiceForCollection.isLoading,
      instructions: "Waiting for an invoice to be generated. Click to generate.",

    },
    {
      label: "Mark as Complete",
      state: completeState,
      onClick: () => {
        if (!canAttemptComplete || isLeadComplete) return;
        setConfirmCompleteOpen(true);
      },
      isClickable: canAttemptComplete && !isLeadComplete,
      instructions: "All other steps done, click to mark lead as complete!"
    },
  ];

  return (
    <>
      <ConfirmModal
        open={confirmCompleteOpen}
        setOpen={setConfirmCompleteOpen}
        title="Mark Lead as Complete"
        message={completeConfirmMessage}
        confirmButtonText="Complete Lead"
        onConfirm={handleCompleteLead}
        isLoading={completing}
      />

      <ConfirmModal
        open={confirmCollectedOpen}
        setOpen={setConfirmCollectedOpen}
        title="Mark as collected?"
        message="Are you sure you want to mark this vehicle as collected?"
        confirmButtonText="Mark collected"
        onConfirm={handleConfirmMarkCollected}
        isLoading={markLeadCollected.isLoading}
      />

      <ConfirmModal
        open={paymentConfirmOpen}
        setOpen={setPaymentConfirmOpen}
        title="Raise payment?"
        message={`Are you sure you want to raise this payment? \nThe payment details will be locked until payment has been sent or cancelled.`}
        confirmButtonText="Raise Payment"
        onConfirm={handleRaisePayment}
        isLoading={raisingPayment}
      />

      <Modal open={codOpen} width={"max-w-2xl"} setOpen={setCodOpen} >
        <CODStatus leadId={lead.id} showAddress />
      </Modal>

      <Modal open={paymentMissingOpen} setOpen={setPaymentMissingOpen} width="max-w-xl">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Payment details required</h3>
          <p className="text-sm text-gray-600 whitespace-pre-line">
            Please fill in `Account Name`, `Account Number`, `Sort Code`, and a valid `Price` before raising payment.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setPaymentMissingOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <LeadCheckInModal
        open={checkInOpen}
        setOpen={setCheckInOpen}
        vehicleId={vehicleId}
        leadId={lead.id}
        steps={checkInStepsData}
        items={checkInItemsData}
        documents={checkInDocumentsData}
        isLoading={checkInSteps.isLoading || (!!checkInId && checkInItems.isLoading)}
        saveCheckInItem={saveCheckInItem}
        uploadCheckInItemDocuments={uploadCheckInItemDocuments}
        downloadCheckInItemDocument={downloadCheckInItemDocument}
        viewSensitiveDocument={viewSensitiveDocument}
        sensitiveDocumentAccess={sensitiveDocumentAccess}
        deleteCheckInItemDocument={deleteCheckInItemDocument}
      />

      <WorkflowStepper title="Lead Workflow" steps={workflowSteps} />
    </>
  );
}
